(() => {
  const SELECTOR = 'ac-reference-footer';
  const GLYPHS = 'abcdefghijklmnopqrstuvwxyz';
  // The discount asset is a 60-image sequence. Draw its selected frame directly
  // instead of constructing 60 Lottie image layers at the Footer's first entry.
  const discountFrames = (() => {
    const supported = Boolean(window.ResizeObserver && window.HTMLImageElement?.prototype.decode && window.HTMLCanvasElement?.prototype.getContext);
    let sourceUrl = '';
    let loading = null;
    let abort = null;
    let frames = null;
    let owners = 0;

    const release = () => {
      if (owners) return;
      abort?.abort();
      abort = null;
      loading = null;
      frames = null;
      sourceUrl = '';
    };

    const prewarm = (url) => {
      if (loading && sourceUrl === url) return loading;
      if (sourceUrl && sourceUrl !== url) release();
      sourceUrl = url;
      const request = new AbortController();
      abort = request;
      const promise = (async () => {
        const response = await fetch(url, { credentials: 'same-origin', signal: request.signal });
        if (!response.ok) throw new Error(`Footer frames ${response.status}`);
        const data = await response.json();
        const assets = (data.assets || []).filter((asset) => /^imgSeq_\d+$/.test(asset.id))
          .sort((a, b) => Number(a.id.slice(7)) - Number(b.id.slice(7)));
        if (assets.length !== 60 || assets.some((asset, index) => asset.id !== `imgSeq_${index}` || !asset.p?.startsWith('data:image/')))
          throw new Error('Unexpected Footer image sequence');
        const decoded = new Array(60);
        const batchSize = matchMedia('(hover: none), (pointer: coarse)').matches ? 8 : 12;
        for (let index = 0; index < 60; index += batchSize) {
          if (request.signal.aborted) throw new DOMException('Footer preload cancelled', 'AbortError');
          const images = assets.slice(index, index + batchSize).map((asset) => {
            const image = new Image();
            image.src = asset.p;
            return image;
          });
          await Promise.all(images.map((image) => image.decode()));
          images.forEach((image, offset) => { decoded[index + offset] = image; });
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        if (request.signal.aborted) throw new DOMException('Footer preload cancelled', 'AbortError');
        frames = decoded;
        return decoded;
      })();
      loading = promise;
      promise.then(() => {
        if (loading === promise) abort = null;
      }, () => {
        if (loading !== promise) return;
        abort = null;
        loading = null;
        frames = null;
        sourceUrl = '';
      });
      return promise;
    };

    const create = (host, url) => {
      owners++;
      let destroyed = false;
      let generation = 0;
      let tween = null;
      let frame = 0;
      let drawn = false;
      let canvas = null;
      let context = null;
      let resize = null;
      let syncCanvasSize = null;

      const setFrame = (next) => {
        const index = Math.max(0, Math.min(59, Math.round(next)));
        if (index === frame && drawn) return;
        frame = index;
        context.clearRect(0, 0, canvas.width, canvas.height);
        const scale = Math.min(canvas.width / 300, canvas.height / 200);
        const width = 300 * scale;
        const height = 200 * scale;
        context.drawImage(frames[index], (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
        drawn = true;
      };

      const ready = prewarm(url).then(() => {
        if (destroyed || !host.isConnected) throw new Error('Footer host disconnected');
        canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(host.clientWidth * devicePixelRatio));
        canvas.height = Math.max(1, Math.round(host.clientHeight * devicePixelRatio));
        context = canvas.getContext('2d');
        if (!context) throw new Error('Footer Canvas unavailable');
        host.append(canvas);
        syncCanvasSize = () => {
          if (destroyed) return;
          const width = Math.max(1, Math.round(host.clientWidth * devicePixelRatio));
          const height = Math.max(1, Math.round(host.clientHeight * devicePixelRatio));
          if (canvas.width === width && canvas.height === height) return;
          canvas.width = width;
          canvas.height = height;
          drawn = false;
          setFrame(frame);
        };
        resize = new ResizeObserver(syncCanvasSize);
        resize.observe(host);
        window.addEventListener('resize', syncCanvasSize, { passive: true });
        setFrame(0);
        host.dataset.acLottieReady = 'true';
        host.dispatchEvent(new CustomEvent('ac:lottie-ready', { bubbles: true }));
        return canvas;
      }).catch((error) => {
        resize?.disconnect();
        if (syncCanvasSize) window.removeEventListener('resize', syncCanvasSize);
        canvas?.remove();
        canvas = null;
        delete host.dataset.acLottieReady;
        throw error;
      });

      return {
        ready,
        get destroyed() { return destroyed; },
        async animateTo(progress, duration = 0.6) {
          const current = ++generation;
          tween?.kill();
          await ready;
          if (destroyed || current !== generation) return;
          const targetFrame = Math.round(Math.max(0, Math.min(1, progress)) * 59);
          if (duration <= 0 || targetFrame === frame) {
            setFrame(targetFrame);
            return;
          }
          const playhead = { frame };
          await new Promise((resolve) => {
            tween = window.gsap.to(playhead, {
              frame: targetFrame, duration, ease: 'power2.out',
              onUpdate: () => setFrame(playhead.frame),
              onComplete: () => { tween = null; resolve(); },
              onInterrupt: resolve,
            });
          });
        },
        destroy() {
          if (destroyed) return;
          destroyed = true;
          generation++;
          tween?.kill();
          resize?.disconnect();
          if (syncCanvasSize) window.removeEventListener('resize', syncCanvasSize);
          canvas?.remove();
          delete host.dataset.acLottieReady;
          owners--;
          release();
        },
      };
    };

    return { supported, prewarm, create, release };
  })();
  class AcReferenceFooter extends HTMLElement {
    #cleanup = [];
    #motionCleanup = [];
    #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    #motionDisabled = null;
    #timeline = null;
    #revealed = false;
    #discountController = null;
    #discountWarmupAbort = null;
    #scrambleEntries = [];
    #scrambleFrame = 0;
    #scrambleCleanupRegistered = false;

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.#bindDiscount();
      this.#motionQuery.addEventListener('change', this.#syncMotion);
      const observer = new MutationObserver(this.#syncMotion);
      observer.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
      this.#cleanup.push(() => {
        this.#motionQuery.removeEventListener('change', this.#syncMotion);
        observer.disconnect();
      });
      this.#syncMotion();
    }

    disconnectedCallback() {
      this.#stopMotion();
      this.#cleanup.splice(0).forEach((dispose) => dispose());
      this.#motionDisabled = null;
      this.#revealed = false;
      delete this.dataset.revealed;
      delete this.dataset.initialized;
    }

    #reduced() {
      return this.#motionQuery.matches || document.body.dataset.acMotion === 'reduced';
    }

    #stopMotion() {
      this.#timeline?.kill();
      this.#timeline = null;
      this.#discountController?.destroy();
      this.#discountController = null;
      this.#discountWarmupAbort?.abort();
      this.#discountWarmupAbort = null;
      discountFrames.release();
      this.#motionCleanup.splice(0).forEach((dispose) => dispose());
    }

    #syncMotion = () => {
      if (!this.isConnected) return;
      const disabled = this.#reduced() || this.dataset.motion === 'none';
      if (disabled === this.#motionDisabled) return;
      this.#motionDisabled = disabled;
      this.#stopMotion();
      if (disabled) {
        this.#revealImmediately();
        return;
      }
      if (!this.#revealed) this.#prepare();
      this.#observe();
    };

    #bindDiscount() {
      const discount = this.querySelector('[data-ac-footer-discount]');
      const action = discount?.querySelector('.ac-ref-footer__discount-action');
      if (!discount || !action) return;

      const focusNewsletter = () => {
        const popup = document.querySelector('ac-newsletter-popup');
        if (popup?.open) {
          popup.open({ trigger: action });
          return;
        }
        const target = action.dataset.acNewsletterFallback || action.getAttribute('aria-controls');
        const input = this.querySelector(`#${CSS.escape(target)}`);
        input?.focus({ preventScroll: false });
        input?.scrollIntoView({ behavior: this.#reduced() ? 'auto' : 'smooth', block: 'center' });
      };
      action.addEventListener('click', focusNewsletter);
      this.#cleanup.push(() => action.removeEventListener('click', focusNewsletter));
    }

    #ensureDiscountController() {
      if (!this.isConnected || this.#reduced() || this.dataset.motion === 'none') return null;
      if (this.#discountController && !this.#discountController.destroyed) return this.#discountController;
      const discount = this.querySelector('[data-ac-footer-discount]');
      const host = discount?.querySelector('[data-ac-footer-discount-host]');
      const path = window.AcLottieManager?.assetUrl?.('footer-discount');
      if (!host || !path || !discountFrames.supported) return null;
      this.#discountWarmupAbort?.abort();
      this.#discountWarmupAbort = null;
      // Failed or unsupported decorative playback keeps the existing poster visible.
      this.#discountController = discountFrames.create(host, path);
      this.#discountController.ready.catch(() => {});
      return this.#discountController;
    }

    #prewarmDiscount() {
      if (this.#discountWarmupAbort || this.#reduced() || this.dataset.motion === 'none') return;
      if (!this.querySelector('[data-ac-footer-discount-host]')) return;
      const path = window.AcLottieManager?.assetUrl?.('footer-discount');
      if (!path || !discountFrames.supported) return;
      const abort = new AbortController();
      this.#discountWarmupAbort = abort;
      const queue = () => {
        if (abort.signal.aborted) return;
        const run = () => {
          if (this.#discountWarmupAbort === abort) this.#discountWarmupAbort = null;
          if (!abort.signal.aborted) discountFrames.prewarm(path).catch(() => {});
        };
        if ('requestIdleCallback' in window) {
          const idle = window.requestIdleCallback(run);
          abort.signal.addEventListener('abort', () => window.cancelIdleCallback(idle), { once: true });
        } else {
          const timer = window.setTimeout(run, 0);
          abort.signal.addEventListener('abort', () => window.clearTimeout(timer), { once: true });
        }
      };
      if (document.readyState === 'complete') queue();
      else window.addEventListener('load', queue, { once: true, signal: abort.signal });
    }

    #playDiscountIntro() {
      if (this.#reduced() || this.dataset.motion === 'none') return;
      const controller = this.#ensureDiscountController();
      if (!controller) return;
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      controller.animateTo(canHover ? 0.5 : 1, this.dataset.motion === 'subtle' ? 0.6 : 0.9).catch(() => {});

      if (!canHover) return;
      const discount = this.querySelector('[data-ac-footer-discount]');
      const action = discount?.querySelector('.ac-ref-footer__discount-action');
      if (!action || action.dataset.acLottieHoverBound === 'true') return;
      action.dataset.acLottieHoverBound = 'true';
      const enter = () => controller.animateTo(1, 0.6).catch(() => {});
      const leave = () => controller.animateTo(0.5, 0.6).catch(() => {});
      action.addEventListener('pointerenter', enter);
      action.addEventListener('pointerleave', leave);
      action.addEventListener('focus', enter);
      action.addEventListener('blur', leave);
      this.#motionCleanup.push(() => {
        action.removeEventListener('pointerenter', enter);
        action.removeEventListener('pointerleave', leave);
        action.removeEventListener('focus', enter);
        action.removeEventListener('blur', leave);
        delete action.dataset.acLottieHoverBound;
      });
    }

    #targets() {
      return {
        figure: this.querySelector('[data-ac-footer-figure]'),
        wordmark: this.querySelector('[data-ac-footer-wordmark]'),
        signup: this.querySelector('.ac-ref-footer__form'),
        heading: this.querySelector('[data-ac-footer-heading]'),
        nav: [...this.querySelectorAll('.ac-ref-footer__nav .ac-action--marker')],
        legal: this.querySelector('[data-ac-footer-legal]'),
      };
    }

    #prepare() {
      const gsap = window.gsap;
      if (!gsap) return;
      const { figure, wordmark, signup, heading, nav, legal } = this.#targets();
      const subtle = this.dataset.motion === 'subtle';
      gsap.set(figure, { yPercent: subtle ? 12 : 100 });
      gsap.set(wordmark, { yPercent: subtle ? 20 : 300 });
      if (signup) gsap.set(signup, { opacity: 0 });
      if (heading) gsap.set(heading, { scaleY: subtle ? 0.8 : 0, transformOrigin: 'bottom center' });
      gsap.set(nav, { opacity: 0 });
      gsap.set(legal, { opacity: 0 });
      this.dataset.prepared = 'true';
    }

    #observe() {
      if (!window.gsap) {
        this.#revealImmediately();
        return;
      }

      let observer;
      let stage = 0;
      let height = 0;
      const steps = [
        { margin: () => innerHeight * 3, run: () => this.#prewarmDiscount() },
        { margin: () => 300, run: () => {
          for (const image of this.querySelectorAll('[data-ac-footer-figure] img, [data-ac-footer-wordmark] img')) {
            image.loading = 'eager';
          }
          this.#ensureDiscountController();
        } },
        { margin: () => 0, run: () => { dispose(); this.#reveal(); } },
      ];
      const observe = () => {
        observer?.disconnect();
        const step = steps[stage];
        if (!step || !this.isConnected) return;
        // Native intersection geometry follows changing pins/content without a
        // scroll listener, body ResizeObserver or synchronous Footer measurement.
        const threshold = stage === 2 && height ? Math.min(1, innerHeight * 0.2 / height) : 0;
        const nextObserver = new IntersectionObserver(([entry]) => {
          if (observer !== nextObserver || stage >= steps.length || !this.isConnected) return;
          if (stage === 2 && Math.abs(height - entry.boundingClientRect.height) > 1) {
            height = entry.boundingClientRect.height;
            observe();
            return;
          }
          height = entry.boundingClientRect.height;
          if (!entry.isIntersecting || entry.intersectionRatio < threshold) return;
          stage++;
          step.run();
          observe();
        }, { rootMargin: `0px 0px ${step.margin()}px 0px`, threshold });
        observer = nextObserver;
        observer.observe(this);
      };
      const dispose = () => {
        stage = steps.length;
        observer?.disconnect();
        window.removeEventListener('resize', observe);
      };
      this.#motionCleanup.push(dispose);
      window.addEventListener('resize', observe, { passive: true });
      observe();
    }

    #reveal() {
      if (!this.isConnected) return;
      if (this.#reduced() || this.dataset.motion === 'none') {
        this.#syncMotion();
        return;
      }
      this.#playDiscountIntro();
      if (this.#revealed) return;
      this.#revealed = true;
      const gsap = window.gsap;
      if (!gsap) {
        this.#revealImmediately();
        return;
      }

      const { figure, wordmark, signup, heading, nav, legal } = this.#targets();
      const subtle = this.dataset.motion === 'subtle';
      gsap.set([figure, wordmark, heading].filter(Boolean), { willChange: 'transform' });
      if (signup) gsap.set(signup, { willChange: 'opacity' });
      this.#timeline = gsap.timeline({
        defaults: { overwrite: 'auto' },
        onComplete: () => {
          gsap.set([figure, wordmark, signup, heading].filter(Boolean), { clearProps: 'willChange' });
          delete this.dataset.prepared;
          this.dataset.revealed = 'true';
        },
      });
      this.#timeline
        .to([figure, wordmark].filter(Boolean), { yPercent: 0, duration: subtle ? 0.7 : 1.2, ease: 'power3.out' }, 0)
        .to(nav, { opacity: 1, duration: 0.01, stagger: 0.04 }, 0)
        .to(legal, { opacity: 1, duration: subtle ? 0.35 : 0.6, ease: 'power2.out' }, 0.5);
      if (signup) this.#timeline.to(signup, { opacity: 1, duration: subtle ? 0.8 : 2, ease: 'power2.out' }, 0);
      if (heading) this.#timeline.to(heading, { scaleY: 1, duration: subtle ? 0.5 : 0.8, ease: 'power3.out' }, subtle ? 0.15 : 0.4);

      if (!subtle) {
        this.#scramble(nav.map((link) => link.querySelector('.ac-action__label')), 0);
        this.#scramble([
          this.querySelector('[data-ac-footer-copyright]'),
          ...this.querySelectorAll('[data-ac-footer-legal] > span .ac-action__label'),
        ], 500);
      }
    }

    #scramble(labels, delay) {
      if (!this.#scrambleCleanupRegistered) {
        this.#scrambleCleanupRegistered = true;
        this.#motionCleanup.push(() => {
          cancelAnimationFrame(this.#scrambleFrame);
          this.#scrambleFrame = 0;
          this.#scrambleEntries.splice(0).forEach(({ restore }) => restore());
          this.#scrambleCleanupRegistered = false;
        });
      }
      labels.forEach((label, labelIndex) => {
        const textNode = [...(label?.childNodes || [])].find((node) => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim());
        const original = textNode?.nodeValue || '';
        if (!/[a-z]/i.test(original)) return;
        // The accessible name stays stable while only a decorative copy changes.
        const accessible = document.createElement('span');
        accessible.className = 'visually-hidden';
        accessible.textContent = original;
        const visual = document.createElement('span');
        visual.setAttribute('aria-hidden', 'true');
        visual.textContent = original;
        textNode.replaceWith(accessible, visual);
        const characters = [...original];
        const start = performance.now() + delay + labelIndex * 40;
        const total = 180 + (characters.length - 1) * 40;
        const restore = () => {
          if (accessible.parentNode) accessible.replaceWith(textNode);
          visual.remove();
        };
        this.#scrambleEntries.push({ characters, start, total, visual, lastText: original, restore });
      });
      if (!this.#scrambleFrame && this.#scrambleEntries.length) {
        this.#scrambleFrame = requestAnimationFrame(this.#tickScramble);
      }
    }

    #tickScramble = (now) => {
      this.#scrambleFrame = 0;
      this.#scrambleEntries = this.#scrambleEntries.filter((entry) => {
        const { characters, start, total, visual, restore } = entry;
        if (now < start) return true;
        const elapsed = now - start;
        const nextText = characters.map((character, index) => {
            const target = GLYPHS.indexOf(character.toLowerCase());
            if (target < 0) return character;
            const progress = Math.max(0, Math.min(1, (elapsed - index * 40) / 180));
            const glyph = GLYPHS[Math.min(target, Math.floor(progress * (target + 1)))];
            return character === character.toUpperCase() ? glyph.toUpperCase() : glyph;
        }).join('');
        if (nextText !== entry.lastText) {
          visual.textContent = nextText;
          entry.lastText = nextText;
        }
        if (elapsed < total) return true;
        restore();
        return false;
      });
      if (this.#scrambleEntries.length) this.#scrambleFrame = requestAnimationFrame(this.#tickScramble);
    };

    #revealImmediately() {
      this.#revealed = true;
      const elements = Object.values(this.#targets()).flat().filter(Boolean);
      window.gsap?.set?.(elements, { clearProps: 'all' });
      delete this.dataset.prepared;
      this.dataset.revealed = 'true';
    }
  }

  if (!customElements.get(SELECTOR)) customElements.define(SELECTOR, AcReferenceFooter);
})();
